import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  Box,
  Card,
  Paper,
  Table,
  TableContainer,
  Collapse,
  Alert,
  AlertTitle,
  Button,
  useTheme,
  useMediaQuery,
  Typography,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
  Popover,
  TextField,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  PaginatedTableColumn,
  PaginatedTableProps,
  TableState,
  createSortComparator,
  ITableFilterType,
  TableFilterType,
  PaginatedTableColumnType,
} from './index';
import PaginatedTableHead from './paginatedTableHead';
import PaginatedTableBody from './paginatedTableBody';
import PaginatedTablePagination from './paginatedTablePagination';
import { ErrorOutline, Refresh, Add } from '@mui/icons-material';
import PaginatedTableFilters from './paginatedTableFilter';
import { useDebounce } from 'src/hooks/useDebounce';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import AddIcon from '@mui/icons-material/Add';
import { showAlert } from 'src/redux/alert/alertReducer';
import { useAppDispatch } from 'src/redux/hooks';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DataService } from 'src/services/data-service';
import Loader from '../Loader';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    position: 'relative',
  },
  container: {
    position: 'relative',
    transition: 'all 0.3s ease',
    overflowX: 'hidden',
  },
  boxedContainer: {
    width: '100%',
    margin: '16px auto',
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    padding: theme.spacing(1, 0, 1, 0),
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden', 
    gap: theme.spacing(0.5),
  },
  boxedTable: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '98%',
    margin: '0 auto',
    background: theme.palette.background.paper,
  },
  boxedCell: {
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
    boxSizing: 'border-box',
    padding: '6px 8px',
    fontSize: '0.92rem',
  },
  wmsContainer: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    paddingTop: theme.spacing(1),
    overflow: 'hidden',
  },
  wmsTable: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    width: '100%',
    background: theme.palette.background.paper,
    '& .MuiTableCell-root': {
      borderRight: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderRight: 'none',
      },
    },
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  table: {
    minWidth: 650,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  emptyState: {
    padding: theme.spacing(4, 1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
  },
  errorState: {
    padding: theme.spacing(2),
  },
  filters: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 12px',
    gap: 8,
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  tableActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleText: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: 0.2,
    marginBottom: theme.spacing(0.2),
    lineHeight: 1.1,
  },
  subtitleText: {
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.2),
  },
  divider: {
    width: '100%',
    height: 2,
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    border: 'none',
    borderRadius: 2,
    marginBottom: theme.spacing(0.5),
    opacity: 0.10,
  }
}));

// Cache interface
interface CacheEntry {
  data: Record<string, any>;
  totalCount: number;
  timestamp: number;
}

interface PaginationCache {
  [key: string]: CacheEntry;
}

// Table state reducer
type TableAction =
  | { type: 'SET_ORDER'; payload: { order: 'asc' | 'desc'; orderBy: string } }
  | { type: 'SET_SELECTED'; payload: string[] }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_ROWS_PER_PAGE'; payload: number }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'SET_FILTER'; payload: { name: string; value: any } }
  | { type: 'SET_FILTERS'; payload: Record<string, any> }
  | { type: 'TOGGLE_COLUMN'; payload: string }
  | { type: 'SET_COLUMN_VISIBILITY'; payload: Record<string, boolean> }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_GLOBAL_FILTER'; payload: string };

function tableReducer(state: TableState, action: TableAction): TableState {
  switch (action.type) {
    case 'SET_ORDER':
      return { ...state, ...action.payload, page: 0 };
    case 'SET_SELECTED':
      return { ...state, selected: action.payload };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_ROWS_PER_PAGE':
      return { ...state, rowsPerPage: action.payload, page: 0 };
    case 'SET_SEARCH':
      return { ...state, searchKey: action.payload, page: 0 };
    case 'SET_FILTER':
      return {
        ...state,
        filterValues: { ...state.filterValues, [action.payload.name]: action.payload.value },
        page: 0,
      };
    case 'SET_FILTERS':
      return { ...state, filterValues: action.payload, page: 0 };
    case 'TOGGLE_COLUMN':
      return {
        ...state,
        columnVisibility: {
          ...state.columnVisibility,
          [action.payload]: !state.columnVisibility[action.payload],
        },
      };
    case 'SET_COLUMN_VISIBILITY':
      return { ...state, columnVisibility: action.payload };
    case 'RESET_FILTERS':
      return { ...state, filterValues: {}, searchKey: '', page: 0 };
    case 'SET_GLOBAL_FILTER':
      return { ...state, globalFilter: action.payload, page: 0 };
    default:
      return state;
  }
}

// Backend pagination response interface
interface BackendPaginationResponse {
  items?: Record<string, any>;
  data?: Record<string, any>; // Alternative format
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// ✅ UPDATED: Enhanced props interface with WMS support
interface ExtendedPaginatedTableProps extends PaginatedTableProps {
  actionsChild?: React.ReactNode;
  rowActionsOutsideMenu?: boolean;
  onAddRow?: (row: any) => Promise<any>;
  onEditRow?: (row: any) => Promise<any>;
  addRowFields?: string[];
  showEditOnTable?: boolean;
  collapsible?: boolean;
  // Backend pagination props
  useBackendPagination?: boolean;
  apiEndpoint?: string;
  cacheTimeout?: number;
  onDataFetch?: (params: { 
    page: number; 
    pageSize: number; 
    filters?: any; 
    search?: string;
    sortBy?: string;
    sortDescending?: boolean;
  }) => Promise<BackendPaginationResponse>;
  wmsMode?: boolean;
  densityLevel?: 'comfortable' | 'standard' | 'compact';
  boxedCells?: boolean;
  boxedCellClass?: string;
  stripedColors?: string[] | ((theme: any) => string[]);
}

const PaginatedTable: React.FC<ExtendedPaginatedTableProps> = ({
  columns,
  data: localData,
  idField = 'id',
  title,
  subtitle,
  loading: externalLoading = true,
  error: externalError = null,
  emptyMessage = 'No data available',
  actions = [],
  filters = [],
  defaultState,
  selection = { showCheckbox: true, selectionMode: 'multiple' },
  dense = false,
  stickyHeader = false,
  maxHeight,
  striped = false,
  bordered = false,
  hoverable = true,
  export: exportOptions = false,
  print = false,
  columnChooser = false,
  fullscreen = false,
  onRowClick,
  onRowDoubleClick,
  onStateChange,
  components = {},
  sx,
  className,
  actionsChild,
  rowActionsOutsideMenu = false,
  onAddRow,
  onEditRow,
  addRowFields,
  showEditOnTable = false,
  collapsible = false,
  useBackendPagination = true,
  apiEndpoint,
  cacheTimeout = 300000,
  onDataFetch,
  wmsMode = false,
  densityLevel = 'standard',
  boxedCells = false,
  boxedCellClass,
  stripedColors,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableRef = useRef<HTMLTableElement>(null);
  const dispatch = useAppDispatch();

  // Backend pagination state
  const [backendData, setBackendData] = useState<Record<string, any>>({});
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [cache, setCache] = useState<PaginationCache>({});

  // Initialize state with defaults
  const initialState: TableState = {
    order: 'asc',
    orderBy: '',
    selected: [],
    page: 0,
    rowsPerPage: 10,
    searchKey: '',
    filterValues: {},
    columnVisibility: columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
    globalFilter: '',
    ...defaultState,
  };

  // Use local storage for persistence
  const [savedState, setSavedState] = useLocalStorage<Partial<TableState>>(
    `Paginated-table-${title?.replace(/\s+/g, '-').toLowerCase()}-state`,
    {}
  );

  const [state, tableDispatch] = useReducer(tableReducer, {
    ...initialState,
    ...savedState,
  });

  // Debounced search
  const debouncedSearch = useDebounce(state.searchKey, 300);
  const debouncedGlobalFilter = useDebounce(state.globalFilter, 300);

  // Save state changes to localStorage
  useEffect(() => {
    const { selected, ...stateToSave } = state;
    setSavedState(stateToSave);
    onStateChange?.(state);
  }, [state, setSavedState, onStateChange]);

  // Memoized visible columns
  const visibleColumns = useMemo(
    () => columns.filter((col) => state.columnVisibility[col.id] !== false),
    [columns, state.columnVisibility]
  );

  // Generate cache key
  const getCacheKey = (page: number, pageSize: number, filters?: any, search?: string, orderBy?: string, order?: string) => {
    return JSON.stringify({ page, pageSize, filters, search });
  };

const fetchBackendData = useCallback(async (page: number, pageSize: number) => {
  const cacheKey = getCacheKey(page, pageSize, state.filterValues, debouncedSearch, state.orderBy, state.order);
  
  const cachedEntry = cache[cacheKey];
  if (cachedEntry && Date.now() - cachedEntry.timestamp < cacheTimeout) {
    setBackendData(cachedEntry.data);
    setTotalCount(cachedEntry.totalCount);
    return;
  }

  setIsLoading(true);
  setError(null);

  try {
    let response: BackendPaginationResponse;
    
    if (onDataFetch) {
      response = await onDataFetch({
        page: page + 1,
        pageSize,
        filters: state.filterValues,
        search: debouncedSearch,
        sortBy: state.orderBy,
        sortDescending: state.order === 'desc',
      });
    } else if (apiEndpoint) {
      const queryParams = new URLSearchParams({
        PageNumber: String(page + 1),
        PageSize: String(pageSize),
      });

      if (debouncedSearch) {
        queryParams.append('SearchTerm', debouncedSearch);
      }

      // Add sorting parameters to query
      if (state.orderBy) {
        queryParams.append('SortBy', state.orderBy);
        queryParams.append('SortDescending', String(state.order === 'desc'));
      }

      Object.entries(state.filterValues || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => {
              queryParams.append(`filters[${key}]`, String(v));
            });
          } else {
            queryParams.append(`filters[${key}]`, String(value));
          }
        }
      });

      const apiResponse = await DataService.get(`${apiEndpoint}?${queryParams.toString()}`);
      
      if (!apiResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await apiResponse.json();
      
      if (result.data) {
        response = result.data;
      } else if (result.items) {
        response = result;
      } else {
        throw new Error('Invalid response format');
      }
    } else {
      throw new Error('No API endpoint or fetch function provided');
    }

    const items = response.items || response.data || {};
    
    const newCacheEntry: CacheEntry = {
      data: items,
      totalCount: response.totalCount,
      timestamp: Date.now(),
    };
    
    setCache(prev => ({
      ...prev,
      [cacheKey]: newCacheEntry,
    }));

    setBackendData(items);
    setTotalCount(response.totalCount);
  } catch (err) {
    setError(err as Error);
    dispatch(showAlert({ 
      message: 'Failed to fetch data', 
      type: 'error' 
    }));
  } finally {
    setIsLoading(false);
  }
}, [apiEndpoint, onDataFetch, state.filterValues, debouncedSearch, state.orderBy, state.order, cache, cacheTimeout, dispatch, columns]);

useEffect(() => {
  if (useBackendPagination) {
    fetchBackendData(state.page, state.rowsPerPage);
  }
}, [state.page, state.rowsPerPage, state.orderBy, state.order, useBackendPagination, fetchBackendData]);

  useEffect(() => {
    if (useBackendPagination) {
      setCache({});
    }
  }, [state.filterValues, debouncedSearch, useBackendPagination]);

  // Determine which data to use
  const data = useBackendPagination ? backendData : localData;
  const loading = useBackendPagination ? isLoading : externalLoading;
  const currentError = useBackendPagination ? error : externalError;

  const filterData = useCallback(
    (dataToFilter: Record<string, any>) => {
      if (useBackendPagination) {
        return dataToFilter;
      }

      return Object.entries(dataToFilter).reduce((acc, [key, row]) => {
        // Global search
        if (debouncedGlobalFilter) {
          const searchStr = debouncedGlobalFilter.toLowerCase();
          const matches = Object.values(row).some((value) => {
            if (value == null) return false;
            return String(value).toLowerCase().includes(searchStr);
          });
          if (!matches) return acc;
        }

        if (debouncedSearch) {
          const searchableColumns = columns.filter((col) => col.searchable !== false);
          const matches = searchableColumns.some((col) => {
            const value = row[col.id];
            if (value == null) return false;
            return String(value).toLowerCase().includes(debouncedSearch.toLowerCase());
          });
          if (!matches) return acc;
        }

        const passesFilters = filters.every((filter) => {
          const value = row[filter.column];
          const filterValue = state.filterValues[filter.id];

          if (filterValue == null || filterValue === '') return true;

          switch (filter.type) {
            case TableFilterType.SELECT:
            case TableFilterType.MULTISELECT:
              if (Array.isArray(filterValue)) {
                return filterValue.length === 0 || filterValue.includes(value);
              }
              return filterValue === value;

            case TableFilterType.TEXT:
              const textFilter = filter as any;
              const matchMode = textFilter.matchMode || 'contains';
              const valueStr = String(value).toLowerCase();
              const filterStr = String(filterValue).toLowerCase();

              switch (matchMode) {
                case 'startsWith':
                  return valueStr.startsWith(filterStr);
                case 'endsWith':
                  return valueStr.endsWith(filterStr);
                case 'equals':
                  return valueStr === filterStr;
                default:
                  return valueStr.includes(filterStr);
              }

            case TableFilterType.NUMBER:
              const numFilter = filter as any;
              const numValue = Number(value);
              const operator = numFilter.operator || 'equals';

              switch (operator) {
                case 'equals':
                  return numValue === Number(filterValue);
                case 'notEquals':
                  return numValue !== Number(filterValue);
                case 'greaterThan':
                  return numValue > Number(filterValue);
                case 'lessThan':
                  return numValue < Number(filterValue);
                case 'between':
                  return (
                    numValue >= Number(filterValue[0]) && numValue <= Number(filterValue[1])
                  );
                default:
                  return true;
              }

            case TableFilterType.DATE:
            case TableFilterType.DATERANGE:
              return true;

            case TableFilterType.BOOLEAN:
              return value === filterValue;

            case TableFilterType.CUSTOM:
              const customFilter = filter as any;
              return customFilter.filterFn(row, filterValue);

            default:
              return true;
          }
        });

        if (passesFilters) {
          acc[key] = row;
        }
        return acc;
      }, {} as Record<string, any>);
    },
    [columns, filters, state.filterValues, debouncedSearch, debouncedGlobalFilter, useBackendPagination]
  );

  const processedData = useMemo(() => {
    if (useBackendPagination) {
      return Object.entries(data);
    }

    const filtered = filterData(data);
    const entries = Object.entries(filtered);

    if (state.orderBy) {
      const column = columns.find((col) => col.id === state.orderBy);
      const comparator = createSortComparator(
        state.order,
        state.orderBy,
        column?.type === PaginatedTableColumnType.CUSTOM
          ? { [state.orderBy]: (column as any).sortComparator }
          : undefined
      );
      entries.sort(([, a], [, b]) => comparator(a, b));
    }

    return entries;
  }, [data, filterData, state.order, state.orderBy, columns, useBackendPagination]);

  const paginatedData = useMemo(() => {
    if (useBackendPagination) {
      return data;
    }

    const start = state.page * state.rowsPerPage;
    const end = start + state.rowsPerPage;
    return Object.fromEntries(processedData.slice(start, end));
  }, [processedData, state.page, state.rowsPerPage, useBackendPagination, data]);

  const dataCount = useBackendPagination ? totalCount : processedData.length;

const handleSort = useCallback((event: React.MouseEvent<unknown>, property: string) => {
  const isAsc = state.orderBy === property && state.order === 'asc';
  tableDispatch({
    type: 'SET_ORDER',
    payload: {
      order: isAsc ? 'desc' : 'asc',
      orderBy: property,
    },
  });
}, [state.order, state.orderBy]);

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const newSelected = useBackendPagination 
          ? Object.keys(paginatedData)
          : processedData.map(([key]) => key);
        tableDispatch({ type: 'SET_SELECTED', payload: newSelected });
      } else {
        tableDispatch({ type: 'SET_SELECTED', payload: [] });
      }
    },
    [processedData, paginatedData, useBackendPagination]
  );

  const handleSelectOne = useCallback(
    (id: string) => {
      if (selection.selectionMode === 'single') {
        tableDispatch({ type: 'SET_SELECTED', payload: [id] });
      } else {
        const selectedIndex = state.selected.indexOf(id);
        let newSelected: string[] = [];

        if (selectedIndex === -1) {
          newSelected = [...state.selected, id];
        } else if (selectedIndex === 0) {
          newSelected = state.selected.slice(1);
        } else if (selectedIndex === state.selected.length - 1) {
          newSelected = state.selected.slice(0, -1);
        } else if (selectedIndex > 0) {
          newSelected = [
            ...state.selected.slice(0, selectedIndex),
            ...state.selected.slice(selectedIndex + 1),
          ];
        }

        tableDispatch({ type: 'SET_SELECTED', payload: newSelected });
      }
    },
    [state.selected, selection.selectionMode]
  );

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    tableDispatch({ type: 'SET_PAGE', payload: newPage });
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    tableDispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(event.target.value, 10) });
  }, []);

  const handleRetry = useCallback(() => {
    if (useBackendPagination) {
      fetchBackendData(state.page, state.rowsPerPage);
    }
  }, [useBackendPagination, fetchBackendData, state.page, state.rowsPerPage]);

  // Error state
  if (currentError && !data) {
    const ErrorComponent = components.ErrorState;
    if (ErrorComponent) {
      return <ErrorComponent error={currentError} />;
    }
    return (
      <Card className={cx(classes.root, className)} sx={sx}>
        <Alert
          severity="error"
          className={classes.errorState}
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<Refresh />}
              onClick={handleRetry}
            >
              Retry
            </Button>
          }
        >
          <AlertTitle>Error Loading Data</AlertTitle>
          {currentError.message || 'An unexpected error occurred'}
        </Alert>
      </Card>
    );
  }

  const isSelected = (id: string) => state.selected.indexOf(id) !== -1;

  // Inline add/edit state
  const [addingRow, setAddingRow] = React.useState(false);
  const [editingRowId, setEditingRowId] = React.useState<string | null>(null);
  const [editRowValues, setEditRowValues] = React.useState<Record<string, any>>({});
  const [editRowErrors, setEditRowErrors] = React.useState<Record<string, string>>({});
  // Only allow addRowFields (if provided), otherwise all editable columns except actions
  const editableColumns = columns.filter(col => col.editable !== false && col.type !== PaginatedTableColumnType.ACTION);
  const addableColumns = addRowFields
    ? columns.filter(col => addRowFields.includes(col.id) && col.editable !== false && col.type !== PaginatedTableColumnType.ACTION)
    : editableColumns;

  // Validation
  function validateRow(values: Record<string, any>) {
    const errors: Record<string, string> = {};
    (addingRow ? addableColumns : editableColumns).forEach(col => {
      if (col.required && !values[col.id]) {
        errors[col.id] = 'Required';
      }
    });
    return errors;
  }

  // Add row handlers
  const handleAddRowClick = () => {
    setAddingRow(true);
    setEditRowValues(addableColumns.reduce((acc, col) => ({ ...acc, [col.id]: '' }), {}));
    setEditRowErrors({});
  };
  const handleEditRowClick = (rowId: string, row: any) => {
    setEditingRowId(rowId);
    setEditRowValues(editableColumns.reduce((acc, col) => ({ ...acc, [col.id]: row[col.id] }), {}));
    setEditRowErrors({});
  };
  const handleEditInputChange = (colId: string, value: any) => {
    setEditRowValues(prev => ({ ...prev, [colId]: value }));
  };
  const handleEditCancel = () => {
    setAddingRow(false);
    setEditingRowId(null);
    setEditRowValues({});
    setEditRowErrors({});
  };
  const handleEditSave = async () => {
    const errors = validateRow(editRowValues);
    setEditRowErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      if (addingRow && onAddRow) {
        await onAddRow(editRowValues);
        dispatch(showAlert({ message: 'Row added successfully', type: 'success' }));
        // Refresh data if using backend pagination
        if (useBackendPagination) {
          fetchBackendData(state.page, state.rowsPerPage);
        }
      } else if (editingRowId && onEditRow) {
        await onEditRow({ ...editRowValues, id: editingRowId });
        dispatch(showAlert({ message: 'Row updated successfully', type: 'success' }));
        // Refresh data if using backend pagination
        if (useBackendPagination) {
          fetchBackendData(state.page, state.rowsPerPage);
        }
      }
      handleEditCancel();
    } catch (e: any) {
      dispatch(showAlert({ message: e?.message || 'Error saving row', type: 'error' }));
    }
  };

  const [collapsed, setCollapsed] = React.useState(false);

  // Handler for header click (only if collapsible)
  const handleHeaderClick = (e: React.MouseEvent) => {
    if (!collapsible) return;
    // Don't collapse if clicking on an action or interactive element
    const interactive = (e.target as HTMLElement).closest('button, [role="button"], input, select, textarea, a');
    if (interactive) return;
    setCollapsed((prev) => !prev);
  };

  // Filter popover state
  const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const filtersActive = Object.keys(state.filterValues).length > 0 || state.searchKey !== '';

  const getWMSStripedColors = () => {
    if (stripedColors) {
      return typeof stripedColors === 'function' ? stripedColors(theme) : stripedColors;
    }
    if (wmsMode) {
      return theme.palette.mode === 'dark' 
        ? ['#1a1a1a', '#202020'] 
        : ['#ffffff', '#f8f9fa'];
    }
    return ['#fff', '#f7f7fa'];
  };

  return (
    <Box className={wmsMode ? classes.wmsContainer : classes.boxedContainer}>
      <Box className={classes.titleRow}
        style={collapsible ? { cursor: 'pointer', userSelect: 'none' } : {}}
        onClick={handleHeaderClick}
      >
        <Box className={classes.titleSection} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          {title && (
            <h2
              role="heading"
              aria-level={2}
              data-testid="page-title"
              className={classes.titleText}
            >
              {title}
            </h2>
          )}
          {subtitle && <span className={classes.subtitleText}>{subtitle}</span>}
          <TextField
            variant="standard"
            placeholder="Search..."
            value={state.searchKey}
            onChange={e => tableDispatch({ type: 'SET_SEARCH', payload: e.target.value })}
            InputProps={{
              disableUnderline: false,
              sx: { fontSize: '0.95rem', minWidth: 160 },
            }}
            sx={{ maxWidth: 240, ml: 2 }}
            size="small"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Filter icon if filters exist */}
          {filters && filters.length > 0 && (
            <Tooltip title="Show filters">
              <span>
                <IconButton
                  onClick={handleFilterClick}
                  color={filtersActive ? "secondary" : "primary"}
                  sx={{
                    bgcolor: filtersActive
                      ? (theme) => theme.palette.secondary.light
                      : (theme) => theme.palette.action.hover,
                    boxShadow: filtersActive ? 2 : undefined,
                    '&:after': filtersActive ? {
                      content: '""',
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: (theme) => theme.palette.secondary.main,
                    } : undefined,
                    '&:hover': {
                      bgcolor: (theme) =>
                        filtersActive
                          ? theme.palette.secondary.main
                          : theme.palette.primary.light,
                      color: (theme) => theme.palette.primary.contrastText,
                    },
                    transition: 'all 0.2s',
                    m: 0,
                    p: 0.5,
                    position: 'relative',
                  }}
                  aria-label="Show filters"
                >
                  <FilterListIcon color={filtersActive ? "secondary" : "inherit"} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {/* Actions area */}
          <Paper
            elevation={2}
            sx={{
              display: 'flex',
              gap: 1,
              p: 0.5,
              borderRadius: 1,
              background: (theme) => theme.palette.background.paper,
              boxShadow: (theme) => theme.shadows[1],
              alignItems: 'center',
              minHeight: 36,
            }}
            onClick={e => e.stopPropagation()}
          >
            {actions && actions.length > 0 && actions
              .filter(action => !action.visible || action.visible(state.selected))
              .map((action) => (
                <Tooltip title={action.title || ''} key={action.key} placement="bottom" arrow>
                  <span>
                    <IconButton
                      color="primary"
                      aria-label={action.title || action.key}
                      data-testid={action.key}            // ← expose stable id
                      onClick={() => action.onClick?.(state.selected)}
                      disabled={action.disabled?.(state.selected)}
                      sx={{
                        bgcolor: (theme) => theme.palette.action.hover,
                        '&:hover': {
                          bgcolor: (theme) => theme.palette.primary.light,
                          color: (theme) => theme.palette.primary.contrastText,
                        },
                        transition: 'all 0.2s',
                        m: 0,
                        p: 0.5,
                      }}
                    >
                      {action.icon}
                    </IconButton>

                  </span>
                </Tooltip>
              ))}
            {/* Add Row Button */}
            {onAddRow && (
              <Tooltip title="Add Row">
                <span>
                  <IconButton color="primary" onClick={handleAddRowClick} disabled={addingRow || editingRowId !== null} sx={{
                    bgcolor: (theme) => theme.palette.action.hover,
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.primary.light,
                      color: (theme) => theme.palette.primary.contrastText,
                    },
                    transition: 'all 0.2s',
                    m: 0,
                    p: 0.5,
                  }}>
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {actionsChild}
            {collapsible && (
              <IconButton
                size="small"
                sx={{ ml: 1, cursor: 'pointer' }}
                onClick={e => {
                  e.stopPropagation();
                  setCollapsed(prev => !prev);
                }}
                aria-label={collapsed ? 'Expand table' : 'Collapse table'}
              >
                {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            )}
          </Paper>
          {/* Filter popover */}
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { zIndex: 2000, minWidth: 320, maxWidth: 400, p: 2, boxShadow: 3, overflow: 'visible' } }}
          >
            <PaginatedTableFilters
              filters={filters}
              filterValues={state.filterValues}
              onFilterChange={(name, value) => tableDispatch({ type: 'SET_FILTER', payload: { name, value } })}
              onResetFilters={() => tableDispatch({ type: 'RESET_FILTERS' })}
              data={Object.values(data)}
              horizontal
            />
          </Popover>
        </Box>
      </Box>
      <div className={classes.divider} />
      <Collapse in={!collapsed} timeout="auto">
        <Box className={classes.container} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
          {/* Loading overlay for backend pagination */}
          {loading && useBackendPagination && (
            <Box className={classes.loadingOverlay}>
              <Loader />
            </Box>
          )}
          <TableContainer
            sx={{
              maxHeight: maxHeight || (stickyHeader ? 'calc(100vh - 300px)' : 'auto'),
              flex: '1 1 auto',
              minHeight: 0,
            }}
          >
            <Table
              className={wmsMode ? classes.wmsTable : classes.boxedTable}
              aria-labelledby="tableTitle"
              size={densityLevel === 'compact' ? 'small' : 'medium'}
              stickyHeader={stickyHeader}
              ref={tableRef}
            >
              <PaginatedTableHead
                columns={visibleColumns}
                order={state.order}
                orderBy={state.orderBy}
                onSelectAllClick={handleSelectAll}
                onRequestSort={handleSort}
                numSelected={state.selected.length}
                rowCount={useBackendPagination ? Object.keys(paginatedData).length : processedData.length}
                showCheckbox={selection.showCheckbox}
                selectionMode={selection.selectionMode}
                boxedCells={wmsMode || boxedCells}
                boxedCellClass={boxedCellClass || classes.boxedCell}
              />
              <PaginatedTableBody
                header={visibleColumns}
                data={paginatedData}
                selected={state.selected}
                handleCheckboxClick={handleSelectOne}
                boxedCells={wmsMode || boxedCells}
                boxedCellClass={boxedCellClass || classes.boxedCell}
                striped={striped}
                stripedColors={getWMSStripedColors()}
                actionsColumnId={visibleColumns.find(col => col.type === PaginatedTableColumnType.ACTION)?.id}
                rowActions={actions}
                rowActionsOutsideMenu={rowActionsOutsideMenu}
                wmsMode={wmsMode}
                densityLevel={densityLevel}
                addingRow={addingRow}
                editingRowId={editingRowId}
                editRowValues={editRowValues}
                editRowErrors={editRowErrors}
                onEditInputChange={handleEditInputChange}
                onEditCancel={handleEditCancel}
                onEditSave={handleEditSave}
                onEditRowClick={handleEditRowClick}
                addableColumns={addableColumns}
                showEditOnTable={showEditOnTable}
              />
            </Table>
          </TableContainer>
          {dataCount === 0 && !loading && (
            <Paper
              elevation={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 280,
                py: 4,
                px: 2,
                background: (theme) => theme.palette.background.default,
                borderRadius: 2,
                margin: 2
              }}
            >
              {components.EmptyState ? (
                <components.EmptyState />
              ) : (
                <>
                  <ErrorOutline sx={{ fontSize: 64, mb: 2, margin: 2, color: (theme) => theme.palette.primary.light, opacity: 0.7 }} />
                  <Typography variant="h5" gutterBottom color="text.primary" sx={{ fontWeight: 600 }}>
                    {emptyMessage || "No data available"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {state.searchKey || Object.keys(state.filterValues).length > 0
                      ? "Try adjusting your search or filters."
                      : "There is currently no data to display here."}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {(state.searchKey || Object.keys(state.filterValues).length > 0) && (
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => tableDispatch({ type: 'RESET_FILTERS' })}
                      >
                        Clear Filters
                      </Button>
                    )}
                    {onAddRow && (
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<AddIcon />}
                        onClick={handleAddRowClick}
                        disabled={addingRow || editingRowId !== null}
                      >
                        Add New
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </Paper>
          )}

          <PaginatedTablePagination
            count={dataCount}
            rowsPerPage={state.rowsPerPage}
            page={state.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </Box>
      </Collapse>
    </Box>
  );

  function handleExport() {
    console.log('Export triggered');
  }

  function handlePrint() {
    window.print();
  }

  function handleFullscreen() {
    if (tableRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        tableRef.current.requestFullscreen();
      }
    }
  }
};

export default PaginatedTable;